import React, { useState, useCallback } from 'react';
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useDropzone } from 'react-dropzone';
import moment from 'moment';
import AWS from 'aws-sdk';
import S3 from 'aws-sdk/clients/s3';
import Modal from "../Modal/Modal"; 
import { s3Config } from '../../utils/helpers/AwsConfig';
import { extractBudget } from '../../redux';
import { 
    buttonStyle,
    containerStyle,
    removeButtonStyle,
    fileListItemStyle,
    dropzoneStyles,
    acceptedFormatsText,
    fileListContainerStyle,
    Conatiner,
    Header,
    Heading,
    SubHeading,
    CloseButton
} from "./FileUpload.styles"

// Define allowed file types outside component to avoid re-creating on every render
const ALLOWED_FILE_TYPES = [
    'text/csv',                                    
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/pdf',
];

interface FileUploadPopupProps {
    open: boolean;
    onClose: () => void;
    singleFile?: boolean;
    projectUuid?: string;
    onBudgetExtracted?: (file?: File) => void;
}

export const FileUpload = ({ open, onClose, singleFile = false, projectUuid, onBudgetExtracted }: FileUploadPopupProps) => {
    const [files, setFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const { companyId, projectId } = useParams();
    const dispatch = useDispatch();
    const projectFilePath = "https://lc-policy-dev.s3.us-east-1.amazonaws.com/Projects+Template+20241212.csv"
    const budgetFilePath = "https://lc-policy-dev.s3.us-east-1.amazonaws.com/budget+template.csv"

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const filteredFiles = acceptedFiles.filter(file => ALLOWED_FILE_TYPES.includes(file.type));
        if (singleFile) {
          setFiles(filteredFiles.slice(0, 1));
        } else {
          setFiles(prevFiles => [...prevFiles, ...filteredFiles]);
        }
    }, [singleFile]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: !singleFile,
    });

    const notifyError = (message) =>
        toast.error(message, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
    });
    
    const notifySuccess = (message, onclose = () => {}) => {
        toast.success(message, {
          position: "top-right",
          autoClose: 1500,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          onClose: onclose,
        });
    };
    

    const removeFile = (fileToRemove: File) => {
        setFiles(prevFiles => prevFiles.filter(file => file !== fileToRemove));
    };

    const uploadFiles = async () => {
        if (files.length === 0) {
            alert('No files selected.');
            return;
        }

        setUploading(true);

        try {
            // If projectUuid is provided, call the extract_budget endpoint
            if (projectUuid && projectId && companyId) {
                const file = files[0];
                const result = await (dispatch as any)(extractBudget(projectUuid, companyId, file, null));
                
                if (result.success) {
                    notifySuccess("Budget extracted successfully");
                    onBudgetExtracted?.(file); // Call callback with the file
                } else {
                    notifyError(result.message || "Failed to extract budget");
                }
            } else {
                // Original S3 upload logic for other cases
                const { accessKeyId, secretAccessKey, region, bucket } = s3Config();
                const S3_BUCKET = bucket;
            
                AWS.config.update({
                    accessKeyId,
                    secretAccessKey,
                    region,
                });

                const s3 = new S3({
                    params: { Bucket: S3_BUCKET },
                    region,
                });

                const folder = projectId ? `COMPANIES/${companyId}/BUDGETS/${projectId}/` : `COMPANIES/${companyId}/PROJECTS/`

                const checkFolderExists = async (folderPath) => {
                    const params = {
                        Bucket: S3_BUCKET,
                        Prefix: folderPath, 
                        Delimiter: '/',
                        MaxKeys: 1
                    }
                    const data = await s3.listObjectsV2(params).promise();
                    return data.Contents.length > 0 || data.CommonPrefixes.length > 0;
                }

                const folderPath = `${folder}`;
                const folderExists = await checkFolderExists(folderPath);

                if (!folderExists) {
                    notifyError(`User / Project not exist`);
                    throw new Error(`Folder ${folderPath} does not exist in S3.`);
                }

                if (projectId) {
                    const file = files[0];
                    const fileExtension = file.name.split('.').pop();
                    const newFileName = `${projectId}_BUDGET.${fileExtension}`;
                   
                    const key = `${folder}${newFileName}`;
                    const params = {
                        Bucket: S3_BUCKET,
                        Key: key,
                        Body: file,
                    };
                    await s3.putObject(params).promise();
                    notifySuccess("File/s uploaded successfully");
                } 
                else {
                    await Promise.all(files.map( async file => {
                        const timestamp = moment().format('YYMMDD-HHmmss-SSS');
                        const fileExtension = file.name.split('.').pop();
                        const newFileName = `${timestamp}_${companyId}.${fileExtension}`;

                        const key = `${folder}${newFileName}`;
                        const params = {
                            Bucket: S3_BUCKET,
                            Key: key,
                            Body: file,
                        };
                        return s3.putObject(params).promise();
                    }));
                    notifySuccess("File/s uploaded successfully");
                }
            }
        } catch (error) {
            notifyError("Error uploading files");
            console.error('Error uploading files:', error);
        } finally {
            setUploading(false);
            onClose();
        }
    };

    const ModalHeader = (
        <Header>
        <CloseButton onClick={onClose}>&times;</CloseButton>
          <div>
            <Heading>Upload Files</Heading>
            { !projectId ? (
            <SubHeading>
                <a href={projectFilePath} target="_blank" rel="noopener noreferrer">
                    Download Projects Template
                </a>
            </SubHeading>
            ) :
            <SubHeading>
                <a href={budgetFilePath} target="_blank" rel="noopener noreferrer">
                    Download Budget Template
                </a>
            </SubHeading> 
            }
          </div>
        </Header>
    );

    return (
        <Conatiner>
        <Modal
            open={open}
            header={ModalHeader}
            onClose={onClose}
            hideCloseIcon
            footer={
                <>
                <div style={containerStyle}>
                    <button onClick={uploadFiles} disabled={uploading} style={buttonStyle}>
                    {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                </div>
                </>
            }
         
        >
            <div
                {...getRootProps()}
                style={{ 
                    ...dropzoneStyles,
                    backgroundColor: isDragActive ? '#e3e3e3' : '#fafafa',
                }}
            >
                <input {...getInputProps()} 
                 multiple={!singleFile}
                />
                {
                    isDragActive ?
                    <p>Drop the {singleFile ? 'file' : 'files'} here...</p> :
                    <p>Drag & drop {singleFile ? 'a file' : 'files'} here, or click to select {singleFile ? 'a file' : 'files'}</p>
                }
            </div>
            <p style={acceptedFormatsText}>Accepted formats: .pdf, .csv, .xls, .xlsx</p>

            {files.length > 0 && (
                <div>
                    <h4>Files to be uploaded:</h4>
                    <div style={fileListContainerStyle}>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {files.map((file, index) => (
                            <li key={index} style={fileListItemStyle}>
                                <span>{file.name}</span> 
                                <button onClick={() => removeFile(file)} style={removeButtonStyle}>
                                &times; 
                                </button>
                            </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </Modal>
        </Conatiner>
    );
};